/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useState, useRef } from 'react';
import type { CertonaRecommendation, CertonaOptions } from '../interface';
import { getCertonaOptions } from '../utils/getCertonaOptions';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useDeviceTypeByMediaQuery } from '@/hooks/useDeviceTypeByMediaQuery';
import { useAppState } from '@/stores/AppState';
import { saveUIState } from '@/stores/actions';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';

/**
 * This hook returns the array of recommendations made by the CERTONA external service depending on
 * the certona page type [there is a constant file where all the page types are "src/constants/certonaPageType.js"]
 * when you are using the hook pass the correct keys depending on the page type, in this link you can see a PDF
 * describing all the possibilities https://track.autozone.com/secure/attachment/55544/Certona-AutoZone-REACT-Implementation-Instructions-v1.3_021120%20%282%29.pdf
 * @param {Object} certonaOptions Object with different keys depending on the certona page type
 */

export const useGetCertonaRecommendations = (
  certonaOptions: CertonaOptions,
  /*
dependecies: the dependency array used in the custom hook. Having no dependencies results in the hook
only firing once and only once upon mount, a behavior similar to onComponentMount().
*/
  dependencies: unknown[] = [],
  /*
shouldExecuteCall: flag for calling Certona's 3rd party api to fetch recommendation data. Should only be needed
for pages where Certona recommendation pages are displayed.
*/
  shouldExecuteCall = true,
  /*
shouldCallCertona: flag for controlling whether window.callCertona() is invoked. When false, the hook will
set up the Certona environment and define the window.certonaRecommendations callback, but will not trigger
the actual Certona API call. Only takes effect when shouldExecuteCall is true.
*/
  shouldCallCertona = true,
): CertonaRecommendation[] => {
  const deviceType = useDeviceTypeByMediaQuery();
  const router = useLegacyRouter();
  const { dispatch: dispatchAppState } = useAppState();
  const [certonaRecommendations, setCertonaRecommendations] = useState<CertonaRecommendation[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const certonaTimeout = useMonetateDecisionFlag('certonaRecommendationsTimeout') ?? 10000;

  useEffect(() => {
    return () => {
      setCertonaRecommendations([]);
      dispatchAppState(saveUIState({ certonaRecommendationsLoaded: false }));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [router.asPath, dispatchAppState]);

  useEffect(() => {
    const configuredCertonaOptions = getCertonaOptions(certonaOptions);

    const fetchRecommendationData = async () => {
      if (typeof window !== 'undefined') {
        if (typeof window.certonaNotCalled === 'undefined') {
          window.certonaNotCalled = true;
        }

        const certonaObject = {
          device: deviceType,
          recommendations: true,
          ...configuredCertonaOptions,
        };
        window.certona = certonaObject;

        if (window.callCertona && window.certonaNotCalled && shouldCallCertona) {
          await window.callCertona();
        }

        window.certonaRecommendations = (response) => {
          setCertonaRecommendations((prev) => {
            if (prev.length === 0) {
              return response.resonance.schemes;
            }
            const merged = new Map(prev.map((s) => [s.scheme, s]));
            response.resonance.schemes.forEach((s) => merged.set(s.scheme, s));
            return Array.from(merged.values());
          });
          window.certonaNotCalled = false;
          dispatchAppState(saveUIState({ certonaRecommendationsLoaded: true }));
          // Clear timeout since Certona responded
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        };

        // Set up timeout to handle case where Certona never responds
        timeoutRef.current = setTimeout(() => {
          dispatchAppState(saveUIState({ certonaRecommendationsLoaded: true }));
          timeoutRef.current = null;
        }, certonaTimeout);
      }
    };

    if (shouldExecuteCall) {
      void fetchRecommendationData();
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.certonaNotCalled = undefined;
      }
      // Clear timeout on cleanup
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEffect(() => {
    return () => setCertonaRecommendations([]);
  }, []);
  return certonaRecommendations;
};
